import React from "react";
import {
    Row,
    Col,
    Card,
    Table,
    Button,
    Avatar,
    Typography, Space, notification, Tag, Input,
    Popconfirm, Modal, Checkbox, Radio
} from "antd";
import * as XLSX from 'xlsx';
import {getAuthenticatedUser} from "../../utils/authHelper";

import {
    DownloadOutlined,
    FileWordOutlined,
    FileExcelOutlined,
    CloseOutlined
} from "@ant-design/icons";
import {useHistory, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {UserDataDtoGenderEnum} from "acc-rest-client";
import {useEffect, useRef, useState} from "react";
import {
    downloadAttachment,
    downloadLettersForHudhur,
    loadLetters,
    loadLettersPaginated,
    loadNewLetterCountsByLanguage,
    updateLetter
} from "../../store/app/app.slice";
import moment from "moment/moment";
import {ProcessedLetterDto} from "ps-letter-mgmtrest-client";
import {useReactToPrint} from 'react-to-print';
import {
    getLanguagesLabel,
    languages,
    getProcessedLetterStatusColor,
    getProcessedLetterStatusLabel,
    getTopicsLabel, letterLanguageFilterDropdown,
    letterStatusFilterDropdown,
    getCustomUnicodeFlagIcon
} from "../helper/labelHelper";

import useUrlTableState from "../../hooks/useUrlTableState";
import gender_male2_icon from "../../assets/ps-images/gender_male2_icon.png";
import gender_female_icon from "../../assets/ps-images/gender_female_icon.png";
import gender_unknown_icon from "../../assets/images/gender_unknown_icon.png";

const {Title} = Typography;

export const calculate_age = (dob1) => {
    var today = new Date();
    var birthDate = moment.utc(dob1);  // create a date object directly from `dob1` argument
    //var birthDate = moment.utc(dob1);  // create a date object directly from `dob1` argument
    var age_now = today.getFullYear() - birthDate.year();
    var m = today.getMonth() - birthDate.month();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.date()))
    {
      age_now--;
    }
    //console.log(age_now);
    return age_now;
}


function getCategoryColor(category) {
    // Define color mapping for categories
    const colorMap = {
        Health: "#ffd700",
        Study: "#4169e1",
        Rishta: "#ff6347",
        Jamaat: "#ff0000",
        Parents: "#32cd32",
        Jamia: "#ffa500",
        Children: "#1e90ff",
        Economy: "#8a2be2",
        Testcategory: "#e5005e",
    };
    return colorMap[category] || "#000000";
}

function LetterTable({filtered}) {
    const dispatch = useDispatch();
    const componentRef = useRef();
    const [isExportModalVisible, setIsExportModalVisible] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState({});
    const [newLetterCountsByLanguage, setNewLetterCountsByLanguage] = useState({});

    // the language counters only apply to new letters, so they are loaded and shown only for that filter
    const showsNewLettersOnly = (statusFilters) => filtered !== "toHudhur" && statusFilters?.length === 1 && statusFilters[0] === "LETTER_RECEIVED";
    const {
        searchText, setSearchText,
        filters, setFilters,
        pagination, setPagination,
        sorting, setSorting
    } = useUrlTableState({
        defaultStatus: filtered ? ['LETTER_PRESENTED_TO_HUDHUR'] : ['LETTER_RECEIVED'],
        defaultSortField: 'originalLetter.metaData.created',
        defaultSortOrder: 'descend'
    });
    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
    });

    const {letters, lettersTotal, lettersPage, isLettersLoading} = useSelector((state) => state.app);
    const history = useHistory();
    const params = useParams();

    // Sync pagination state with Redux store (convert from 0-based to 1-based)
    useEffect(() => {
        if (lettersPage !== undefined && lettersPage !== null && filtered !== "toHudhur") {
            const expectedPage = lettersPage + 1; // Backend is 0-based, Ant Design is 1-based
            if (pagination.current !== expectedPage) {
                setPagination(prev => ({
                    ...prev,
                    current: expectedPage
                }));
            }
        }
    }, [lettersPage, filtered]);

    const allColumns = {
        'ID': true,
        'Present to Huzoor': true,
        'Summary Title English': true,
        'Summary Title Urdu': true,
        'Summary Topics': true,
        'Summary Notes': true,
        'Answer Language': true,
        'Custom Answer': true,
        'Advice from Huzoor': true,
        'Internal Status': true,
        'Assigned To': true,
        'Attachment ID Letter': true,
        'Filtered At': true,
        'Filtered By': true,
        'Filtered By Name': true,
        'Attachment ID Summary': true,
        'Summary At': true,
        'Summary Title Original': true,
        'Summary By': true,
        'Summary By Name': true,
        'Answer At': true,
        'Answer Original': true,
        'Answer By': true,
        'Answer By Name': true,
        'Attachment ID Answer': true,
        'Created': true,
        'Created By': true,
        'Created By Name': true,
        'Modified': true,
        'Modified By': true,
        'Modified By Name': true,
        'Letter ID': true,
        'Letter Content': true,
        'Letter Category': true,
        'Letter Language': true,
        'Letter Created': true,
        'Letter Updated': true,
        'Letter Created By': true,
        'Letter Updated By': true,
        'Letter Version': true,
        'Sender ID': true,
        'Sender First Name': true,
        'Sender Last Name': true,
        'Sender Urdu Name': true,
        'Sender AIMS ID': true,
        'Sender Birthdate': true,
        'Sender Street Line 1': true,
        'Sender Street Line 2': true,
        'Sender ZIP Code': true,
        'Sender City': true,
        'Sender State': true,
        'Sender Country': true,
        'Sender Country Code': true,
        'Sender Country Flag': true,
        'Sender Jamaat': true,
        'Sender Gender': true,
        'Sender Contact Number': true,
        'Sender Approval Status': true,
        'Sender Email': true,
        'Sender Email Verified': true,
        'Sender Created Timestamp': true
    };

    const activeRequestRef = useRef(null);

    const loadData = (page, pageSize, sortField, sortOrder, search, languageFilters, statusFilters) => {
        const sortParam = sortField && sortOrder ? `${sortField};${sortOrder === 'ascend' ? 'ASC' : 'DESC'}` : undefined;
        const params = {
            page: (page || 1) - 1, // Ant Design uses 1-based, backend uses 0-based
            size: filtered === "toHudhur" ? -1 : (pageSize || 10),
            sort: sortParam,
            search: search || undefined,
            language: languageFilters && languageFilters.length > 0 ? languageFilters : undefined,
            status: filtered === "toHudhur" ? ['LETTER_PRESENTED_TO_HUDHUR'] : (statusFilters && statusFilters.length > 0 ? statusFilters : undefined)
        };

        // Abort any previous in-flight request
        if (activeRequestRef.current) {
            activeRequestRef.current.abort();
        }

        const promise = dispatch(loadLettersPaginated({ finalData: params }));
        activeRequestRef.current = promise;
        if (showsNewLettersOnly(statusFilters)) {
            dispatch(loadNewLetterCountsByLanguage({callbackSuccess: setNewLetterCountsByLanguage}));
        }
    };

    const navigateToLetterView = (record) => {
        history.push("/letters/" + record.id)
    }

    // Track previous values to detect what changed and avoid duplicate requests
    const prevFiltersRef = useRef({ language: filters.language, status: filters.status });
    const prevSearchTextRef = useRef(searchText);
    const searchTimeoutRef = useRef(null);

    // Single consolidated effect for all data loading triggers
    useEffect(() => {
        const filtersChanged = prevFiltersRef.current.language !== filters.language
            || prevFiltersRef.current.status !== filters.status;
        const searchChanged = prevSearchTextRef.current !== searchText;

        prevFiltersRef.current = { language: filters.language, status: filters.status };
        prevSearchTextRef.current = searchText;

        // Debounce search changes
        if (searchChanged) {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            searchTimeoutRef.current = setTimeout(() => {
                // For toHudhur view, always keep current filters (status is hardcoded in thunk)
                // For regular view, clear status filter when searching to search across all statuses
                const searching = filtered !== "toHudhur" && searchText.trim().length > 0;
                const mustClearFilters = searching
                    && (filters.status.length > 0 || filters.language.length > 0);
                if (mustClearFilters) {
                    setFilters(prev => ({ ...prev, status: [], language: [] }));
                    return;
                }
                loadData(1, pagination.pageSize, sorting.field, sorting.order, searchText,
                    searching ? [] : filters.language,
                    searching ? [] : filters.status);
                setPagination(prev => ({ ...prev, current: 1 }));
            }, 500);
            // If only search changed, wait for debounce — don't fire immediately
            if (!filtersChanged) {
                return () => {
                    if (searchTimeoutRef.current) {
                        clearTimeout(searchTimeoutRef.current);
                    }
                };
            }
        }

        // For filter, sorting, or initial load changes — fire immediately
        if (filtersChanged) {
            // Cancel any pending debounced search since we're loading now anyway
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = null;
            }
            loadData(1, pagination.pageSize, sorting.field, sorting.order, searchText, filters.language, filters.status);
            setPagination(prev => ({ ...prev, current: 1 }));
        } else if (!searchChanged) {
            // Initial load or sorting/filtered change
            loadData(pagination.current, pagination.pageSize, sorting.field, sorting.order, searchText, filters.language, filters.status);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [filtered, sorting.field, sorting.order, filters.language, filters.status, searchText]);

    const columnsLetters = [
        //Letters Overview: Name | Country | Jamaat | Age | Language | Status | Date
        // Other details are not needed.
        {
            title: "Name (Age)",
            dataIndex: "originalLetter",
            sorter: true,
            sortDirections: ['ascend', 'descend'],
            render: (letter, record) => (letter ?

                <a type="link" onClick={() => navigateToLetterView(record)}>
                    <Avatar.Group>
                        <div style={{marginRight: "3px"}}>
                            <Avatar
                                src={letter.sender.gender ? letter.sender.gender === UserDataDtoGenderEnum.Male ? gender_male2_icon : letter.sender.gender === UserDataDtoGenderEnum.Female ? gender_female_icon : gender_unknown_icon : gender_unknown_icon}
                                shape="square" size="large">{letter.sender.gender}</Avatar>
                        </div>
                        <div className="avatar-info">
                            <Title
                                level={5}>{
                                    letter.sender.firstName + " " +
                                    letter.sender.lastName +
                                    " (" +
                                        (letter.sender.birthdate ? calculate_age(letter.sender.birthdate) : "0")
                                + ")"}
                            </Title>
                            <p>{letter.sender.email}</p>
                        </div>
                    </Avatar.Group>
                </a> : "N/A"),
        },
        {
            title: "Jamā'at, Country",
            dataIndex: ["originalLetter", "sender"],
            key: "country",
            sorter: true,
            sortDirections: ['ascend', 'descend'],
            render: (sender) => (<span>
                {sender.jamaat + ", "}
                {sender.country ? sender.country.defaultLabel : ""}&nbsp;
                {getCustomUnicodeFlagIcon(sender.country?.values?.flag)
                }
            </span>),
        },
        // {
        //     title: "Jamaat",
        //     dataIndex: ["originalLetter", "sender", "jamaat"],
        //     sorter: (a, b) => a.originalLetter.sender.jamaat.localeCompare(b.originalLetter.sender.jamaat),
        //     key: "jamaat",
        // },
        // {
        //     title: "Age",
        //     dataIndex: ["originalLetter", "sender", "birthdate"],
        //     sorter: (a, b) => a.originalLetter.sender.birthdate.localeCompare(b.originalLetter.sender.birthdate),
        //     render: (birthdate) => (calculate_age(birthdate)),
        // },
        {
            title: "Language",
            dataIndex: ["originalLetter", "language"],
            filters: letterLanguageFilterDropdown,
            filteredValue: filters.language,
            sorter: true,
            sortDirections: ['ascend', 'descend'],
            render: (language) => (getLanguagesLabel(language)),
        },
        {
            title: "Status",
            key: "status",
            dataIndex: ["internalStatus"],
            ...(filtered !== "toHudhur" ? { filters: letterStatusFilterDropdown, filteredValue: filters.status } : {}),
            sorter: true,
            sortDirections: ['ascend', 'descend'],
            render: (status, record) => (
                <Button type="text" style={{backgroundColor: getProcessedLetterStatusColor(record), color: 'white'}}>
                    {getProcessedLetterStatusLabel(record)}
                </Button>
            ),
        },
        {
            title: "Date",
            dataIndex: ["originalLetter", "metaData", "created"],
            key: "date",
            defaultSortOrder: "descend",
            render: (date) => (date ? moment(date).format("YYYY-MM-DD") : "N/A"),
            sorter: true,
            sortDirections: ['ascend', 'descend'],
        },
        // {
        //     title: "Topic",
        //     dataIndex: ["originalLetter", "category"],
        //     key: "category",
        //     sorter: (a, b) => a.originalLetter.category.localeCompare(b.originalLetter.category),
        //     render: (category) => (
        //         <Button type="text" style={{
        //             color: getCategoryColor(category),
        //             borderColor: getCategoryColor(category),
        //             borderStyle: 'solid'
        //         }}>
        //             {getTopicsLabel(category)}
        //         </Button>
        //     ),
        // },
        // {
        //     title: "Content",
        //     dataIndex: ["originalLetter", "content"],
        //     key: "content",
        //     sorter: (a, b) => a.originalLetter.content.localeCompare(b.originalLetter.content),
        //     render: (content) => (content.substring(0, 20) + " ...")
        // },
        // {
        //     title: "Huzoor",
        //     dataIndex: ["presentToHudhur"],
        //     key: "hudhur",
        //     filters: [
        //         {
        //             text: 'Present to Huzoor',
        //             value: true,
        //         },
        //         {
        //             text: 'Summarize',
        //             value: false,
        //         },
        //         {
        //             text: 'Not defined',
        //             value: null,
        //         },
        //     ],
        //     filteredValue: filters.hudhur,
        //     onFilter: (value, record) => record.presentToHudhur === value,
        //     sorter: (a, b) => a.presentToHudhur - b.presentToHudhur,
        //     render: (presentToHudhur, record) => (
        //         <Space>
        //             {record.internalStatus === "LETTER_PRESENTED_TO_HUDHUR" ? (
        //                 <Popconfirm
        //                     title="Are you sure you want to move this letter to summary?"
        //                     onConfirm={() => handleMoveToSummary(record)}
        //                     okText="Yes"
        //                     cancelText="No"
        //                 >
        //                     <div style={{
        //                         backgroundColor: '#f0f0f0',
        //                         color: presentToHudhur ? 'green' : presentToHudhur === false ? 'red' : 'grey',
        //                         borderRadius: '30%',
        //                         width: '24px',
        //                         height: '24px',
        //                         display: 'flex',
        //                         justifyContent: 'center',
        //                         alignItems: 'center',
        //                         fontSize: '12px',
        //                         fontWeight: 'bold',
        //                         cursor: 'pointer'
        //                     }}>
        //                         {presentToHudhur ? '✓' : presentToHudhur === false ? '❌' : ''}
        //                     </div>
        //                 </Popconfirm>
        //             ) : (
        //                 <div style={{
        //                     backgroundColor: '#f0f0f0',
        //                     color: presentToHudhur ? 'green' : presentToHudhur === false ? 'red' : 'grey',
        //                     borderRadius: '30%',
        //                     width: '24px',
        //                     height: '24px',
        //                     display: 'flex',
        //                     justifyContent: 'center',
        //                     alignItems: 'center',
        //                     fontSize: '12px',
        //                     fontWeight: 'bold'
        //                 }}>
        //                     {presentToHudhur ? '✓' : presentToHudhur === false ? '❌' : ''}
        //                 </div>
        //             )}
        //         </Space>
        //     ),
        // },
        // {
        //     title: "Attachments",
        //     key: "attachmentIdAnswer",
        //     dataIndex: ["attachmentIdAnswer"],
        //     render: (attachmentIdAnswer, record) => (
        //         <>
        //
        //             {record.attachmentIdLetterToHudhur ? <a><Tag color="green" onClick={() => {
        //                   e.stopPropagation();
        //                   handleDownloadAttachment(record.attachmentIdLetterToHudhur);
        //                 }} icon={<DownloadOutlined/>}>Letter</Tag></a>: ""}
        //
        //             {record.attachmentIdSummary ?  <a><Tag color="red" onClick={() => {
        //                   e.stopPropagation();
        //                   handleDownloadAttachment(record.attachmentIdSummary)
        //                 }} icon={<DownloadOutlined/>}>Summary</Tag></a> : ""}
        //
        //             {record.attachmentIdAnswer ? <><br/><a><Tag onClick={() => {
        //                   e.stopPropagation();
        //                   handleDownloadAttachment(record.attachmentIdAnswer)
        //                 }} color="blue" icon={<DownloadOutlined/>}>Answer</Tag></a></> : ""}
        //         </>
        //     ),
        // },
        // {
        //     title: "By",
        //     dataIndex: ["assignedTo"],
        //     key: "by",
        // },
    ];
    
    const handleMoveToSummary = (record) => {
        dispatch(updateLetter({
            finalData: {
                id: record.id,
                updatedLetter: {
                    id: record.id,
                    internalStatus: "LETTER_READY_FOR_SUMMARY"
                }
            },
            callbackSuccess: (response) => {
                notification['success']({
                    message: 'Success',
                    description: 'Letter moved to summary successfully.',
                });
                // Refresh the table
                loadData(pagination.current, pagination.pageSize, sorting.field, sorting.order, searchText, filters.language, filters.status);
            },
            callbackError: (error) => {
                notification['error']({
                    message: 'Error',
                    description: error,
                });
            }
        }));
    };

    const handleDownloadLettersForHudhur = () => {
        dispatch(downloadLettersForHudhur({
            finalData: {},
            callbackSuccess: (response) => {
                console.log("Download started", response);
                notification['success']({
                    message: 'Download started',
                    description:
                        'Download for Letters to Huzoor-e-Aqdas started.',
                });
            },
            callbackError: (error) => {
                console.error("Error downloading attachment", error);
                notification['error']({
                    message: 'Error downloading attachment ',
                    description:error,
                });
            }
        }));
    };

    const handleDownloadAttachment = (attachmentId) => {
        dispatch(downloadAttachment({
            finalData: {id: attachmentId},
            callbackSuccess: (response) => {
                console.log("Download started", response);
                notification['success']({
                    message: 'Download started',
                    description:
                        'Download for Attachment with '+attachmentId+' started.',
                });
            },
            callbackError: (error) => {
                console.error("Error downloading attachment", error);
                notification['error']({
                    message: 'Error downloading attachment ',
                    description:error,
                });
            }
        }));
    };

    const showExportModal = () => {
        // Get visible columns from the table in the correct order
        const visibleColumns = columnsLetters.map(col => {
            switch(col.title) {
                case "Language": return "Letter Language";
                case "Status": return "Internal Status";
                case "Date": return "Letter Created";
                default: return col.title;
            }
        });

        // Additional columns to preselect
        const additionalPreselectedColumns = [
            "Sender First Name",
            "Sender Last Name",
            "Sender Urdu Name",
            "Sender Birthdate",
            "Sender Email",
            "Sender Jamaat",
            "Sender Country"
        ];

        // Get all other columns that are not in the table
        const otherColumns = Object.keys(allColumns).filter(key => !visibleColumns.includes(key));

        // Create initial selection with visible columns first, then others
        const initialSelection = {};
        [...visibleColumns, ...otherColumns].forEach(key => {
            initialSelection[key] = visibleColumns.includes(key) || additionalPreselectedColumns.includes(key);
        });

        setSelectedColumns(initialSelection);
        setIsExportModalVisible(true);
    };

    const handleColumnSelection = (column, checked) => {
        setSelectedColumns(prev => ({
            ...prev,
            [column]: checked
        }));
    };

    const handleSelectAll = (checked) => {
        const newSelection = {};
        Object.keys(allColumns).forEach(key => {
            newSelection[key] = checked;
        });
        setSelectedColumns(newSelection);
    };

    const handleExportToExcel = () => {
        // For export, we need to fetch all data matching current filters
        // This is a simplified version - in production, you might want a separate export endpoint
        let exportData = letters;

        const data = exportData.map(record => {
            const row = {};
            // Then add any other selected columns
            Object.keys(selectedColumns).forEach(key => {
                if (selectedColumns[key] && !row.hasOwnProperty(key)) {
                    switch(key) {
                        case 'Sender First Name':
                            row[key] = record.originalLetter?.sender?.firstName;
                            break;
                        case 'Sender Last Name':
                            row[key] = record.originalLetter?.sender?.lastName;
                            break;
                        case 'Sender Email':
                            row[key] = record.originalLetter?.sender?.email;
                            break;
                        case 'Sender Urdu Name':
                            row[key] = record.originalLetter?.sender?.urduName;
                            break;
                        case 'Sender Country':
                            row[key] = record.originalLetter?.sender?.country?.defaultLabel;
                            break;
                        case 'Sender Jamaat':
                            row[key] = record.originalLetter?.sender?.jamaat;
                            break;
                        case 'Letter Category':
                            row[key] = record.originalLetter?.category;
                            break;
                        case 'Sender Birthdate':
                            row[key] = record.originalLetter?.sender?.birthdate ? moment(record.originalLetter.sender.birthdate).format("YYYY-MM-DD") : "N/A";
                            break;
                        case 'Letter Language':
                            row[key] = record.originalLetter?.language;
                            break;
                        case 'Letter Content':
                            row[key] = record.originalLetter?.content;
                            break;
                        case 'Present to Huzoor':
                            row[key] = record.presentToHudhur ? 'Yes' : 'No';
                            break;
                        case 'Attachment ID Letter':
                            row[key] = record.attachmentIdLetterToHudhur;
                            break;
                        case 'Internal Status':
                            row[key] = record.internalStatus;
                            break;
                        case 'Letter Created':
                            row[key] = record.originalLetter?.metaData?.created ? moment(record.originalLetter.metaData.created).format("YYYY-MM-DD HH:mm:ss") : "N/A";
                            break;
                        case 'Assigned To':
                            row[key] = record.assignedTo;
                            break;
                        case 'ID':
                            row[key] = record.id;
                            break;
                        case 'Summary Title English':
                            row[key] = record.summary?.titleEnglish;
                            break;
                        case 'Summary Title Urdu':
                            row[key] = record.summary?.titleUrdu;
                            break;
                        case 'Summary Topics':
                            row[key] = record.summary?.topics ? Array.from(record.summary.topics).join(', ') : '';
                            break;
                        case 'Summary Notes':
                            row[key] = record.summaryNotes;
                            break;
                        case 'Answer Language':
                            row[key] = record.answerLanguage;
                            break;
                        case 'Custom Answer':
                            row[key] = record.customAnswer;
                            break;
                        case 'Advice from Huzoor':
                            row[key] = record.adviceFromHudhur ? 'Yes' : 'No';
                            break;
                        case 'Filtered At':
                            row[key] = record.filteredAt ? moment(record.filteredAt).format("YYYY-MM-DD HH:mm:ss") : "N/A";
                            break;
                        case 'Filtered By':
                            row[key] = record.filteredBy;
                            break;
                        case 'Filtered By Name':
                            row[key] = record.filteredByName;
                            break;
                        case 'Attachment ID Summary':
                            row[key] = record.attachmentIdSummary;
                            break;
                        case 'Summary At':
                            row[key] = record.summaryAt ? moment(record.summaryAt).format("YYYY-MM-DD HH:mm:ss") : "N/A";
                            break;
                        case 'Summary Title Original':
                            row[key] = record.summaryTitleOriginal;
                            break;
                        case 'Summary By':
                            row[key] = record.summaryBy;
                            break;
                        case 'Summary By Name':
                            row[key] = record.summaryByName;
                            break;
                        case 'Answer At':
                            row[key] = record.answerAt ? moment(record.answerAt).format("YYYY-MM-DD HH:mm:ss") : "N/A";
                            break;
                        case 'Answer Original':
                            row[key] = record.answerOriginal;
                            break;
                        case 'Answer By':
                            row[key] = record.answerBy;
                            break;
                        case 'Answer By Name':
                            row[key] = record.answerByName;
                            break;
                        case 'Attachment ID Answer':
                            row[key] = record.attachmentIdAnswer;
                            break;
                        case 'Created':
                            row[key] = record.created ? moment(record.created).format("YYYY-MM-DD HH:mm:ss") : "N/A";
                            break;
                        case 'Created By':
                            row[key] = record.createdBy;
                            break;
                        case 'Created By Name':
                            row[key] = record.createdByName;
                            break;
                        case 'Modified':
                            row[key] = record.modified ? moment(record.modified).format("YYYY-MM-DD HH:mm:ss") : "N/A";
                            break;
                        case 'Modified By':
                            row[key] = record.modifiedBy;
                            break;
                        case 'Modified By Name':
                            row[key] = record.modifiedByName;
                            break;
                        case 'Letter ID':
                            row[key] = record.originalLetter?.letterId;
                            break;
                        case 'Letter Updated':
                            row[key] = record.originalLetter?.metaData?.updated ? moment(record.originalLetter.metaData.updated).format("YYYY-MM-DD HH:mm:ss") : "N/A";
                            break;
                        case 'Letter Created By':
                            row[key] = record.originalLetter?.metaData?.createdBy;
                            break;
                        case 'Letter Updated By':
                            row[key] = record.originalLetter?.metaData?.updatedBy;
                            break;
                        case 'Letter Version':
                            row[key] = record.originalLetter?.metaData?.version;
                            break;
                        case 'Sender ID':
                            row[key] = record.originalLetter?.sender?.id;
                            break;
                        case 'Sender AIMS ID':
                            row[key] = record.originalLetter?.sender?.aimsId;
                            break;
                        case 'Sender Street Line 1':
                            row[key] = record.originalLetter?.sender?.streetLine1;
                            break;
                        case 'Sender Street Line 2':
                            row[key] = record.originalLetter?.sender?.streetLine2;
                            break;
                        case 'Sender ZIP Code':
                            row[key] = record.originalLetter?.sender?.zipCode;
                            break;
                        case 'Sender City':
                            row[key] = record.originalLetter?.sender?.city;
                            break;
                        case 'Sender State':
                            row[key] = record.originalLetter?.sender?.state;
                            break;
                        case 'Sender Country Code':
                            row[key] = record.originalLetter?.sender?.country?.code;
                            break;
                        case 'Sender Country Flag':
                            row[key] = record.originalLetter?.sender?.country?.values?.flag;
                            break;
                        case 'Sender Gender':
                            row[key] = record.originalLetter?.sender?.gender;
                            break;
                        case 'Sender Contact Number':
                            row[key] = record.originalLetter?.sender?.contactNumber;
                            break;
                        case 'Sender Approval Status':
                            row[key] = record.originalLetter?.sender?.approvalStatus;
                            break;
                        case 'Sender Email Verified':
                            row[key] = record.originalLetter?.sender?.emailVerified ? 'Yes' : 'No';
                            break;
                        case 'Sender Created Timestamp':
                            row[key] = record.originalLetter?.sender?.createdTimestamp ? moment(record.originalLetter.sender.createdTimestamp).format("YYYY-MM-DD HH:mm:ss") : "N/A";
                            break;
                        default:
                            //row[key] = "N/A";
                    }
                }
            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Letters");
        XLSX.writeFile(workbook, "letters_export.xlsx");

        notification['success']({
            message: 'Export Successful',
            description: 'Letters exported to Excel successfully.',
        });
        setIsExportModalVisible(false);
    };
    
    return (
        <>
            <div className="tabled">
                <Row gutter={[24, 0]}>
                    <Col xs="24" xl={24}>
                        <Card
                            bordered={false}
                            className="criclebox tablespace mb-24"
                            title={filtered === "toHudhur" ?
                                <span>Letters going to Huzoor<sup>aa</sup> ({lettersTotal || 0})</span> :
                                <span>Letters Overview ({lettersTotal || 0})
                                    {showsNewLettersOnly(filters.status) ?
                                        <span style={{marginLeft: 16, fontWeight: 'normal', fontSize: '14px', color: '#8c8c8c'}}>
                                            {Object.keys(languages).map((lng) => getLanguagesLabel(lng) + " (" + (newLetterCountsByLanguage[lng] || 0) + ")").join(" | ")}
                                        </span> : ""}
                                </span>}
                            extra={<Space>
                                <Input
                                    placeholder="Search..."
                                    value={searchText}
                                    onChange={e => {
                                        setSearchText(e.target.value);
                                    }}
                                    style={{ width: 200 }}
                                />
                                {filtered !== "toHudhur" ?
                                    <Radio.Group
                                        buttonStyle="solid"
                                        value={filters.status?.length === 1 ? filters.status[0] : filters.status?.length === 0 ? "all" : "all"}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const newStatus = value === "all" ? [] : [value];
                                            setFilters({
                                                ...filters,
                                                status: newStatus
                                            });
                                        }}
                                    >
                                            <Radio.Button value="LETTER_RECEIVED">New Letters</Radio.Button>
                                            <Radio.Button value="LETTER_READY_FOR_SUMMARY">Ready For Summary</Radio.Button>
                                            <Radio.Button value="LETTER_READY_FOR_ANSWER">Ready For Answer</Radio.Button>
                                            <Radio.Button value="all">All</Radio.Button>
                                    </Radio.Group>
                                    : null
                                }

                                    {/*{filtered !== "toHudhur" ?*/}
                                    {/*    <Button className={"ant-tag-blue"} onClick={e => {*/}
                                    {/*        setFilters({*/}
                                    {/*            language: [],*/}
                                    {/*            hudhur: [],*/}
                                    {/*            status: ["LETTER_READY_FOR_SUMMARY"]*/}
                                    {/*        });*/}
                                    {/*    }}*/}
                                    {/*            type="default">Ready For Summary*/}
                                    {/*    </Button> : null*/}
                                    {/*}*/}

                                {getAuthenticatedUser()?.isPortalManager() && (
                                    <Button className={"ant-tag-green"} onClick={showExportModal}
                                           type="default"><FileExcelOutlined/> Export as Excel
                                    </Button>
                                )}
                                {filtered === "toHudhur" ? <Button className={"ant-tag-blue"} onClick={handleDownloadLettersForHudhur}
                                                                   type="default"><FileWordOutlined/> Export as Word
                                </Button> : ""}
                            </Space>}
                        >
                            <div className="table-responsive" ref={componentRef}>
                                <Table
                                    columns={columnsLetters}
                                    dataSource={letters}
                                    loading={isLettersLoading}
                                    pagination={filtered === "toHudhur" ? false : {
                                        current: pagination.current,
                                        pageSize: pagination.pageSize,
                                        total: lettersTotal || 0,
                                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                                        showSizeChanger: true,
                                        pageSizeOptions: ['10', '20', '50', '100'],
                                    }}
                                    onChange={(paginationState, tableFilters, sorterState) => {
                                        let filtersChanged = false;
                                        let sortingChanged = false;

                                        // Handle filters
                                        const newLanguage = tableFilters["originalLetter.language"] || [];
                                        const newStatus = tableFilters["status"] || [];
                                        const languageChanged = JSON.stringify(newLanguage) !== JSON.stringify(filters.language);
                                        const statusChanged = JSON.stringify(newStatus) !== JSON.stringify(filters.status);
                                        if (languageChanged || statusChanged) {
                                            setFilters({
                                                language: newLanguage,
                                                status: newStatus
                                            });
                                            filtersChanged = true;
                                        }

                                        // Handle sorting
                                        if (sorterState && sorterState.field) {
                                            let sortField = sorterState.field;
                                            if (Array.isArray(sortField)) {
                                                sortField = sortField.join('.');
                                            }
                                            // Map frontend column keys to backend field paths
                                            if (sortField === 'originalLetter' || sortField === 'originalLetter.sender.firstName') {
                                                sortField = 'originalLetter.sender.firstName';
                                            } else if (sortField === 'originalLetter.sender' || sortField === 'originalLetter.sender.country.defaultLabel') {
                                                sortField = 'originalLetter.sender.country.defaultLabel';
                                            } else if (sortField === 'originalLetter.language') {
                                                sortField = 'originalLetter.language';
                                            } else if (sortField === 'internalStatus') {
                                                sortField = 'internalStatus';
                                            } else if (sortField === 'originalLetter.metaData.created') {
                                                sortField = 'originalLetter.metaData.created';
                                            }
                                            if (sorterState.order && (sortField !== sorting.field || sorterState.order !== sorting.order)) {
                                                setSorting({
                                                    field: sortField,
                                                    order: sorterState.order
                                                });
                                                sortingChanged = true;
                                            }
                                        }

                                        // Handle pagination — only call loadData directly for pure pagination changes.
                                        // Filter and sorting changes are handled by the useEffect.
                                        if (!filtersChanged && !sortingChanged) {
                                            const newPage = paginationState.current;
                                            const newPageSize = paginationState.pageSize;
                                            if (newPage !== pagination.current || newPageSize !== pagination.pageSize) {
                                                setPagination({ current: newPage, pageSize: newPageSize });
                                                loadData(newPage, newPageSize, sorting.field, sorting.order, searchText, filters.language, filters.status);
                                            }
                                        }
                                    }}
                                    className="ant-border-space"
                                    onRow={(record, rowIndex) => {
                                        return {
                      onClick: (event) => {
                        navigateToLetterView(record);
                                            }, // click row
                      onDoubleClick: (event) => {}, // double click row
                      onContextMenu: (event) => {}, // right button click row
                      onMouseEnter: (event) => {}, // mouse enter row
                      onMouseLeave: (event) => {}, // mouse leave row
                                        };
                                    }}
                                />
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
            <Modal
                title="Select Columns for Export"
                open={isExportModalVisible}
                onOk={handleExportToExcel}
                onCancel={() => setIsExportModalVisible(false)}
                width={600}
            >
                <div style={{ marginBottom: 16 }}>
                    <Checkbox
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        checked={Object.values(selectedColumns).every(v => v)}
                    >
                        Select All
                    </Checkbox>
                </div>
                <div style={{ maxHeight: 400, overflow: 'auto' }}>
                    {Object.keys(allColumns).map(column => (
                        <div key={column} style={{ marginBottom: 8 }}>
                            <Checkbox
                                checked={selectedColumns[column]}
                                onChange={(e) => handleColumnSelection(column, e.target.checked)}
                            >
                                {column}
                            </Checkbox>
                        </div>
                    ))}
                </div>
            </Modal>
        </>
    );
}

export default LetterTable;